import React, { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ClipboardCheck,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  Building2,
  ShieldCheck,
  Award,
  Upload,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  FileText,
  Calendar,
  Info,
  Plus,
} from 'lucide-react';
import api from '../services/api';
import { KwaliteitspaspoortBlok } from './KwaliteitspaspoortBlok';
import { BeroepsregistratieCertificaatKaart } from './BeroepsregistratieCertificaatKaart';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DocumentRenewal {
  id: number;
  document_type: 'kvk' | 'insurance' | 'certification';
  professional_type: string | null;
  document_url: string;
  uploaded_at: string;
  expires_at: string | null;
  status: 'pending_review' | 'approved' | 'rejected';
  admin_notes: string | null;
}

interface TypeRegistration {
  id: number;
  professional_type: string;
  is_registered: boolean;
  registration_number: string | null;
  registration_organization: string | null;
  is_approved: boolean;
  not_affiliated: boolean;
  latest_renewal: DocumentRenewal | null;
  /** Einddatum van het bewijs (handmatig traject, migratie 171) */
  geldig_tot?: string | null;
  /** Automatische BIG-check (migratie 168) */
  big_check_status?: string | null;
}

interface Screening {
  id: string;
  status: 'pending' | 'approved' | 'rejected' | 'incomplete';
  submitted_at: string;
  reviewed_at: string | null;
  admin_feedback: string | null;
  kvk_nummer: string;
  bedrijfsnaam: string;
  kvk_last_checked_at: string | null;
  kvk_check_status: 'ok' | 'deregistered' | 'failed' | null;
  insurance_company: string | null;
  policy_number: string | null;
  insurance_expires_at: string | null;
  selected_professional_types: string[];
  registrations: TypeRegistration[];
  insurance_renewal: DocumentRenewal | null;
  kvk_renewal: DocumentRenewal | null;
}

interface OverviewData {
  screenings: Screening[];
  can_start_new_screening: boolean;
}

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

const fmtDate = (d?: string | null) =>
  d
    ? new Date(d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

const addMonths = (date: Date, months: number): Date => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
};

const isExpiringWithin = (dateStr: string | null, days: number): boolean => {
  if (!dateStr) return false;
  const expiry = new Date(dateStr);
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() + days);
  return expiry <= cutoff && expiry >= new Date();
};

const isExpired = (dateStr: string | null): boolean => {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
};

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

interface StatusBadgeProps {
  status: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const { t } = useTranslation('pages');
  const map: Record<string, { label: string; classes: string }> = {
    pending:          { label: t('screening.statusPending'), classes: 'bg-amber-100 text-amber-800' },
    approved:         { label: t('screening.statusApproved'), classes: 'bg-green-100 text-green-800' },
    rejected:         { label: t('screening.statusRejected'), classes: 'bg-red-100 text-red-800' },
    incomplete:       { label: t('screening.statusIncomplete'), classes: 'bg-gray-100 text-gray-600' },
    pending_review:   { label: t('screening.statusPendingReview'), classes: 'bg-amber-100 text-amber-700' },
  };
  const cfg = map[status] ?? { label: status, classes: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.classes}`}>
      {cfg.label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Upload form (inline)
// ---------------------------------------------------------------------------

interface UploadFormProps {
  screeningId: string;
  documentType: 'kvk' | 'insurance' | 'certification';
  professionalType?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const UploadForm: React.FC<UploadFormProps> = ({
  screeningId,
  documentType,
  professionalType,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation('pages');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expiresAt, setExpiresAt] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setError(t('screening.selectFile'));
      return;
    }

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('document', file);
    formData.append('screening_id', screeningId);
    formData.append('document_type', documentType);
    if (professionalType) formData.append('professional_type', professionalType);
    if (expiresAt) formData.append('expires_at', expiresAt);

    try {
      const response = await fetch('/api/screening/upload-renewal', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        onSuccess();
      } else {
        const msgs = data.errors ? data.errors.join(', ') : data.message ?? t('screening.uploadFailed');
        setError(msgs);
      }
    } catch {
      setError(t('screening.networkError'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3"
    >
      <p className="text-xs font-semibold text-[#0B1F3A]">{t('screening.uploadDocument')}</p>

      <div>
        <label className="block text-xs text-gray-600 mb-1">{t('screening.fileLabel')}</label>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          className="block w-full text-xs text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-[#0B1F3A] file:text-white hover:file:opacity-90"
          required
        />
      </div>

      {(documentType === 'insurance' || documentType === 'certification') && (
        <div>
          <label className="block text-xs text-gray-600 mb-1">{t('screening.expiryDateOptional')}</label>
          <input
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="block w-full text-xs border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#0B1F3A]"
          />
        </div>
      )}

      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <XCircle size={12} /> {error}
        </p>
      )}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={uploading}
          className="bg-[#0B1F3A] text-white rounded-lg px-4 py-2 text-xs font-semibold hover:opacity-90 disabled:opacity-60 flex items-center gap-1"
        >
          {uploading ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />}
          {uploading ? t('screening.uploading') : t('screening.upload')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-500 text-xs hover:text-gray-700"
        >
          {t('screening.cancel')}
        </button>
      </div>
    </form>
  );
};

// ---------------------------------------------------------------------------
// KVK section
// ---------------------------------------------------------------------------

interface KvkSectionProps {
  screening: Screening;
  onUploadSuccess: () => void;
}

const KvkSection: React.FC<KvkSectionProps> = ({ screening, onUploadSuccess }) => {
  const { t } = useTranslation('pages');
  const [showUpload, setShowUpload] = useState(false);

  const nextCheckDate = screening.kvk_last_checked_at
    ? addMonths(new Date(screening.kvk_last_checked_at), 3)
    : null;

  const kvkStatusConfig: Record<string, { label: string; classes: string; icon: React.ReactNode }> = {
    ok:           { label: t('screening.kvkRegistered'), classes: 'text-green-700 bg-green-50', icon: <CheckCircle size={13} /> },
    deregistered: { label: t('screening.kvkDeregistered'), classes: 'text-red-700 bg-red-50', icon: <XCircle size={13} /> },
    failed:       { label: t('screening.kvkManualCheck'), classes: 'text-orange-700 bg-orange-50', icon: <AlertTriangle size={13} /> },
  };
  const kvkCfg = screening.kvk_check_status ? kvkStatusConfig[screening.kvk_check_status] : null;

  return (
    <div className="border border-gray-100 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Building2 size={16} className="text-[#0B1F3A]" />
        <h4 className="text-sm font-semibold text-gray-800">{t('screening.kvkTitle')}</h4>
      </div>

      <div className="space-y-1.5 text-sm">
        <div className="flex items-center gap-2 text-gray-700">
          <span className="font-medium">{screening.bedrijfsnaam}</span>
          <span className="text-gray-400 text-xs">KVK {screening.kvk_nummer}</span>
        </div>

        {kvkCfg && (
          <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${kvkCfg.classes}`}>
            {kvkCfg.icon}
            {kvkCfg.label}
          </div>
        )}

        {screening.kvk_last_checked_at && (
          <p className="text-xs text-gray-500">
            {t('screening.lastCheck')}: {fmtDate(screening.kvk_last_checked_at)}
            {nextCheckDate && ` · ${t('screening.nextCheck')}: ${fmtDate(nextCheckDate.toISOString())}`}
          </p>
        )}

        {screening.kvk_renewal && (
          <div className="flex items-center gap-2 mt-1">
            <FileText size={12} className="text-gray-400" />
            <span className="text-xs text-gray-500">
              {t('screening.renewedExtract')}: {fmtDate(screening.kvk_renewal.uploaded_at)} –{' '}
              <StatusBadge status={screening.kvk_renewal.status} />
            </span>
          </div>
        )}
      </div>

      {!showUpload ? (
        <button
          onClick={() => setShowUpload(true)}
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#0B1F3A] hover:underline"
        >
          <Upload size={12} />
          {t('screening.uploadKvk')}
        </button>
      ) : (
        <UploadForm
          screeningId={screening.id}
          documentType="kvk"
          onSuccess={() => { setShowUpload(false); onUploadSuccess(); }}
          onCancel={() => setShowUpload(false)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Insurance section
// ---------------------------------------------------------------------------

interface InsuranceSectionProps {
  screening: Screening;
  onUploadSuccess: () => void;
}

const InsuranceSection: React.FC<InsuranceSectionProps> = ({ screening, onUploadSuccess }) => {
  const { t } = useTranslation('pages');
  const [showUpload, setShowUpload] = useState(false);

  const expiresAt = screening.insurance_expires_at;
  const expiring = isExpiringWithin(expiresAt, 60);
  const expired = isExpired(expiresAt);

  return (
    <div className="border border-gray-100 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={16} className="text-[#0B1F3A]" />
        <h4 className="text-sm font-semibold text-gray-800">{t('screening.insuranceTitle')}</h4>
      </div>

      <div className="space-y-1.5 text-sm">
        {screening.insurance_company ? (
          <p className="text-gray-700 font-medium">
            {screening.insurance_company}
            {screening.policy_number && <span className="text-gray-400 text-xs ml-2">· {screening.policy_number}</span>}
          </p>
        ) : (
          <p className="text-xs text-gray-500 italic">{t('screening.insurerNotSpecified')}</p>
        )}

        {expiresAt && (
          <div className="flex items-center gap-1.5">
            <Calendar size={12} className="text-gray-400" />
            <span className="text-xs text-gray-600">{t('screening.expires')}: {fmtDate(expiresAt)}</span>
            {expired && (
              <span className="text-xs font-semibold text-red-600 flex items-center gap-0.5">
                <XCircle size={11} /> {t('screening.expired')}
              </span>
            )}
            {!expired && expiring && (
              <span className="text-xs font-semibold text-orange-600 flex items-center gap-0.5">
                <AlertTriangle size={11} /> {t('screening.expiringSoon')}
              </span>
            )}
          </div>
        )}

        {screening.insurance_renewal && (
          <div className="flex items-center gap-2 mt-1">
            <FileText size={12} className="text-gray-400" />
            <span className="text-xs text-gray-500">
              {t('screening.renewalUploaded')}: {fmtDate(screening.insurance_renewal.uploaded_at)} –{' '}
              <StatusBadge status={screening.insurance_renewal.status} />
            </span>
          </div>
        )}
      </div>

      {!showUpload ? (
        <button
          onClick={() => setShowUpload(true)}
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#0B1F3A] hover:underline"
        >
          <Upload size={12} />
          {t('screening.uploadInsurance')}
        </button>
      ) : (
        <UploadForm
          screeningId={screening.id}
          documentType="insurance"
          onSuccess={() => { setShowUpload(false); onUploadSuccess(); }}
          onCancel={() => setShowUpload(false)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Registration (role) section
// ---------------------------------------------------------------------------

interface RegistrationSectionProps {
  reg: TypeRegistration;
  screeningId: string;
  onUploadSuccess: () => void;
}

const RegistrationSection: React.FC<RegistrationSectionProps> = ({ reg, screeningId, onUploadSuccess }) => {
  const { t } = useTranslation('pages');
  const [showUpload, setShowUpload] = useState(false);

  const label = t(`screening.types.${reg.professional_type}`, { defaultValue: reg.professional_type });

  return (
    <div className="border border-gray-100 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Award size={16} className="text-[#0B1F3A]" />
          <h4 className="text-sm font-semibold text-gray-800">{label}</h4>
        </div>
        {reg.is_approved ? (
          <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full font-medium">
            <CheckCircle size={11} /> {reg.big_check_status === 'actief' ? t('screening.statusBigVerified') : t('screening.statusApproved')}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full font-medium">
            <Clock size={11} /> {t('screening.pendingManualReview')}
          </span>
        )}
      </div>

      <div className="space-y-1 text-xs text-gray-600">
        {reg.registration_organization && (
          <p>{t('screening.organisation')}: <span className="font-medium text-gray-800">{reg.registration_organization}</span></p>
        )}
        {reg.registration_number && (
          <p>{t('screening.registrationNumber')}: <span className="font-medium text-gray-800">{reg.registration_number}</span></p>
        )}
        {reg.not_affiliated && (
          <p className="text-gray-500 italic">{t('screening.noAffiliation')}</p>
        )}
        {reg.geldig_tot && (
          <p className={isExpired(reg.geldig_tot) ? 'text-red-600 font-medium' : isExpiringWithin(reg.geldig_tot, 30) ? 'text-amber-700 font-medium' : ''}>
            {t('screening.expires')}: {fmtDate(reg.geldig_tot)}
          </p>
        )}

        {reg.latest_renewal && (
          <div className="flex items-center gap-2 mt-1">
            <FileText size={11} className="text-gray-400" />
            <span>
              {t('screening.renewedProof')}: {fmtDate(reg.latest_renewal.uploaded_at)} –{' '}
              <StatusBadge status={reg.latest_renewal.status} />
            </span>
          </div>
        )}
      </div>

      {!showUpload ? (
        <button
          onClick={() => setShowUpload(true)}
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#0B1F3A] hover:underline"
        >
          <Upload size={12} />
          {t('screening.uploadProof')}
        </button>
      ) : (
        <UploadForm
          screeningId={screeningId}
          documentType="certification"
          professionalType={reg.professional_type}
          onSuccess={() => { setShowUpload(false); onUploadSuccess(); }}
          onCancel={() => setShowUpload(false)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Single screening card
// ---------------------------------------------------------------------------

interface ScreeningCardProps {
  screening: Screening;
  onUploadSuccess: () => void;
}

const ScreeningCard: React.FC<ScreeningCardProps> = ({ screening, onUploadSuccess }) => {
  const { t } = useTranslation('pages');
  const [expanded, setExpanded] = useState(screening.status === 'approved');

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div
        className="p-6 flex items-start justify-between cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#0B1F3A10', color: '#0B1F3A' }}
          >
            <ClipboardCheck size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-gray-900">
                {t('screening.screeningTitle')} – {screening.bedrijfsnaam || t('screening.unknownCompany')}
              </p>
              <StatusBadge status={screening.status} />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t('screening.submitted')}: {fmtDate(screening.submitted_at)}
              {screening.reviewed_at && ` · ${t('screening.reviewed')}: ${fmtDate(screening.reviewed_at)}`}
            </p>
            {screening.selected_professional_types.length > 0 && (
              <p className="text-xs text-gray-400 mt-0.5">
                {screening.selected_professional_types
                  .map((tp) => t(`screening.types.${tp}`, { defaultValue: tp }))
                  .join(', ')}
              </p>
            )}
          </div>
        </div>
        <div className="text-gray-400 flex-shrink-0 ml-2 mt-1">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 p-6 space-y-6">
          {screening.status === 'pending' && (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <Clock size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">{t('screening.pendingTitle')}</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  {t('screening.pendingText', { date: fmtDate(screening.submitted_at) })}
                </p>
              </div>
            </div>
          )}

          {screening.status === 'rejected' && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
              <XCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">{t('screening.rejectedTitle')}</p>
                {screening.admin_feedback && (
                  <p className="text-xs text-red-700 mt-1">{screening.admin_feedback}</p>
                )}
                <a
                  href="/professional/screening"
                  className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-[#0B1F3A] hover:underline"
                >
                  {t('screening.resubmitLink')}
                </a>
              </div>
            </div>
          )}

          {screening.status === 'approved' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={16} className="text-green-500" />
                <p className="text-sm font-semibold text-green-800">{t('screening.approvedTitle')}</p>
                {screening.reviewed_at && (
                  <span className="text-xs text-gray-400">{t('screening.onDate')} {fmtDate(screening.reviewed_at)}</span>
                )}
              </div>

              <KvkSection screening={screening} onUploadSuccess={onUploadSuccess} />
              <InsuranceSection screening={screening} onUploadSuccess={onUploadSuccess} />

              {screening.registrations.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Award size={15} className="text-[#0B1F3A]" />
                    <p className="text-sm font-semibold text-gray-700">{t('screening.registrationsPerRole')}</p>
                  </div>
                  <div className="space-y-3">
                    {screening.registrations.map((reg) => (
                      <RegistrationSection
                        key={reg.id}
                        reg={reg}
                        screeningId={screening.id}
                        onUploadSuccess={onUploadSuccess}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-4 animate-pulse">
    {[1, 2].map((i) => (
      <div key={i} className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-gray-200 rounded-lg" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-48" />
            <div className="h-3 bg-gray-100 rounded w-32" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export const ScreeningOverviewView: React.FC = () => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery<{ success: boolean; data: OverviewData }>({
    queryKey: ['screening-overview'],
    queryFn: async () => {
      const res = await api.get('/screening/my-overview');
      return res.data;
    },
  });

  const handleUploadSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['screening-overview'] });
  };

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (isError || !data?.success) {
    return (
      <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-6">
        <AlertTriangle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-red-800">{t('screening.loadError')}</p>
          <p className="text-xs text-red-600 mt-1">{t('screening.loadErrorText')}</p>
        </div>
      </div>
    );
  }

  const { screenings, can_start_new_screening } = data.data;

  if (screenings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] text-center p-8">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
          style={{ backgroundColor: '#0B1F3A10', color: '#0B1F3A' }}
        >
          <ClipboardCheck size={28} />
        </div>
        <h3 className="text-base font-bold text-gray-800 mb-2">{t('screening.noScreening')}</h3>
        <p className="text-sm text-gray-500 max-w-sm mb-4">{t('screening.noScreeningText')}</p>
        <a
          href="/professional/screening"
          className="inline-flex items-center gap-2 bg-[#0B1F3A] text-white rounded-lg px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          <Plus size={15} />
          {t('screening.startScreening')}
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl p-4">
        <Info size={16} className="text-[#0B1F3A] flex-shrink-0 mt-0.5" />
        <p className="text-xs text-gray-600 leading-relaxed">{t('screening.infoBanner')}</p>
      </div>

      <div className="space-y-4">
        {screenings.map((screening) => (
          <ScreeningCard
            key={screening.id}
            screening={screening}
            onUploadSuccess={handleUploadSuccess}
          />
        ))}
      </div>

      {/* Kwaliteitspaspoort: direct onder de screeningkaart(en) met "Registraties per rol" */}
      {screenings.some((sc) => sc.status === 'approved') && (
        <>
          <KwaliteitspaspoortBlok />
          {/* Beroepsregistratiecertificaat: ledger-export (PDF/CSV) met publieke hash-verificatie */}
          <BeroepsregistratieCertificaatKaart />
        </>
      )}

      {can_start_new_screening && (
        <div className="bg-white rounded-xl border border-dashed border-[#0B1F3A] border-opacity-30 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-gray-800">{t('screening.addRole')}</p>
            <p className="text-xs text-gray-500 mt-0.5">{t('screening.addRoleText')}</p>
          </div>
          <a
            href="/professional/screening"
            className="inline-flex items-center gap-2 bg-[#0B1F3A] text-white rounded-lg px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity flex-shrink-0"
          >
            <Plus size={15} />
            {t('screening.startNewScreening')}
          </a>
        </div>
      )}
    </div>
  );
};

export default ScreeningOverviewView;
